import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { Transaction } from "../services/earnings.service"

interface RefundDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transaction: Transaction | null
  onConfirm: (transactionId: string) => Promise<void>
  onSuccess?: () => void
}

export function RefundDialog({
  open,
  onOpenChange,
  transaction,
  onConfirm,
  onSuccess,
}: RefundDialogProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleConfirm = async () => {
    if (!transaction) return
    setIsLoading(true)
    try {
      await onConfirm(transaction.id)
      toast.success("Refund processed successfully")
      onOpenChange(false)
      onSuccess?.()
    } catch {
      toast.error("Failed to process refund. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] p-0 pb-[30px] pt-[50px]">
        <DialogHeader className="flex flex-col gap-2.5 px-[30px]">
          <DialogTitle className="text-xl text-center font-semibold text-primary">
            Refund Payment
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-body leading-6">
            Are you sure you want to refund{" "}
            <span className="font-semibold text-primary">
              {transaction
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: transaction.currency,
                  }).format(transaction.amount ?? transaction.earned)
                : ""}
            </span>{" "}
            for this transaction? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-3.75 flex flex-col! items-center w-full px-[30px]">
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            isLoading={isLoading}
            className="max-w-[280px] w-full py-3.75 uppercase bg-destructive hover:bg-destructive/90"
          >
            Confirm Refund
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="max-w-[280px] w-full py-3.75 border-[#F2F0EB] text-primary uppercase"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
